import { useContext, useEffect, useRef } from 'react';
import { BackHandlerContext } from '../components/common/BackHandlerProvider';

/**
 * useBackHandler — claim the Android hardware back button while something is open.
 *
 * Most of this app's "screens" are not routes. Home alone stacks a mobile menu,
 * a saved panel (with its own detail view), a works panel, an expanded item and
 * a category modal — all React state, all at the URL `/home`, all invisible to
 * the history stack. Android's back button therefore used to pop straight past
 * them and exit the app, destroying the live map and everything the user had
 * opened on top of it.
 *
 * This hook lets a component say "while I am open, back belongs to me":
 *
 *   useBackHandler(showSavedPanel, () => setShowSavedPanel(false));
 *
 * Handlers form a LIFO stack, so the most recently opened thing is always the
 * first to close — which is exactly what a user expects. When no handler is
 * registered, BackHandlerProvider falls back to route navigation and finally to
 * double-press-to-exit.
 *
 * Deliberately no history entries are pushed. `utils/navHistory.js` reads
 * `window.history.state.idx` to decide whether `navigate(-1)` will actually move,
 * and `useSmartHome` checks whether the entry behind the current one is Home.
 * Synthetic entries would corrupt both.
 *
 * @param {boolean} active   true while this handler should own the back button
 * @param {() => void} onBack what to do when back is pressed
 */
export default function useBackHandler(active, onBack) {
  const registry = useContext(BackHandlerContext);

  // Keep the latest callback without re-registering: re-registering would move
  // this handler to the top of the stack on every render of the owning
  // component, silently reordering the unwind sequence.
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    if (!active || !registry) return undefined;
    return registry.register(() => onBackRef.current?.());
  }, [active, registry]);
}

export { useBackHandler };
